import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardContent,
  IconButton,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import { getOffices, createOffice, updateOffice, deleteOffice, checkOfficeExists } from '../services/officeAPIs';

const OfficeManagement = () => {
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingOffice, setEditingOffice] = useState(null);
  const [formData, setFormData] = useState({ name: '' });
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const colors = {
    primary1: '#3d52a0',
    primary2: '#7091e6',
    primary3: '#8697c4',
    primary4: '#adbbda',
    primary5: '#ede8f5'
  };

  useEffect(() => {
    fetchOffices();
  }, []);

  const fetchOffices = async () => {
    try {
      setLoading(true);
      const data = await getOffices();
      setOffices(data);
    } catch (error) {
      showSnackbar(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (office = null) => {
    setEditingOffice(office);
    setFormData({ name: office ? office.name : '' });
    setErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingOffice(null);
    setFormData({ name: '' });
    setErrors({});
  };

  const validateForm = async () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Office name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Office name must be at least 2 characters long';
    } else {
      // Check if office name already exists (excluding current office if editing)
      const exists = await checkOfficeExists(formData.name.trim());
      if (exists && (!editingOffice || editingOffice.name.toLowerCase() !== formData.name.trim().toLowerCase())) {
        newErrors.name = 'An office with this name already exists';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!(await validateForm())) return;

    try {
      const officeData = { name: formData.name.trim() };
      
      if (editingOffice) {
        await updateOffice(editingOffice.id, officeData);
        showSnackbar('Office updated successfully!', 'success');
      } else {
        await createOffice(officeData);
        showSnackbar('Office created successfully!', 'success');
      }
      
      fetchOffices();
      handleCloseDialog();
    } catch (error) {
      showSnackbar(error.message, 'error');
    }
  };

  const handleDelete = async (office) => {
    if (!window.confirm(`Are you sure you want to delete "${office.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteOffice(office.id);
      showSnackbar('Office deleted successfully!', 'success');
      fetchOffices();
    } catch (error) {
      showSnackbar(error.message, 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress sx={{ color: colors.primary1 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ color: colors.primary1, fontWeight: 'bold' }}>
          Office Management
        </Typography>
        <Typography variant="body1" sx={{ color: colors.primary3 }}>
          Manage your company offices and locations
        </Typography>
      </Box>

      {/* Add Office Button */}
      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            backgroundColor: colors.primary1,
            '&:hover': { backgroundColor: colors.primary2 },
            px: 3,
            py: 1.5
          }}
        >
          Add New Office
        </Button>
      </Box>

      {/* Offices Grid */}
      <Grid container spacing={3}>
        {offices.map((office) => (
          <Grid item xs={12} sm={6} md={4} key={office.id}>
            <Card
              sx={{
                height: '100%',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 3
                },
                border: `1px solid ${colors.primary4}`
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <BusinessIcon sx={{ color: colors.primary2, mr: 1, fontSize: 32 }} />
                  <Typography variant="h6" component="h2" sx={{ color: colors.primary1, fontWeight: 'bold' }}>
                    {office.name}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Chip
                    icon={<PeopleIcon />}
                    label={`${office.employees?.length || 0} employees`}
                    size="small"
                    sx={{
                      backgroundColor: colors.primary5,
                      color: colors.primary1,
                      fontWeight: '500'
                    }}
                  />
                </Box>

                <Divider sx={{ my: 2, borderColor: colors.primary4 }} />

                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Tooltip title="Edit Office">
                    <IconButton
                      onClick={() => handleOpenDialog(office)}
                      sx={{ color: colors.primary2 }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Office">
                    <IconButton
                      onClick={() => handleDelete(office)}
                      sx={{ color: colors.primary3 }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {offices.length === 0 && !loading && (
        <Paper
          sx={{
            p: 4,
            textAlign: 'center',
            backgroundColor: colors.primary5,
            border: `2px dashed ${colors.primary4}`
          }}
        >
          <BusinessIcon sx={{ fontSize: 64, color: colors.primary3, mb: 2 }} />
          <Typography variant="h6" sx={{ color: colors.primary2, mb: 1 }}>
            No Offices Found
          </Typography>
          <Typography variant="body2" sx={{ color: colors.primary3 }}>
            Get started by adding your first office location.
          </Typography>
        </Paper>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: colors.primary1, color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <BusinessIcon sx={{ mr: 1 }} />
            {editingOffice ? 'Edit Office' : 'Add New Office'}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <TextField
            fullWidth
            label="Office Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            error={!!errors.name}
            helperText={errors.name}
            margin="normal"
            placeholder="Enter office name (e.g., New York Office)"
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button onClick={handleCloseDialog} sx={{ color: colors.primary3 }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{
              backgroundColor: colors.primary1,
              '&:hover': { backgroundColor: colors.primary2 }
            }}
          >
            {editingOffice ? 'Update Office' : 'Create Office'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OfficeManagement;